













































import { SdsFlag } from '@superbet-group/web.lib.ui-components';
import { isMobileLayout } from '@superbet-group/web.lib.vue-utilities';
import {
    defineComponent,
    ref,
    computed,
    nextTick,
    watch,
    ComponentPublicInstance,
    PropType,
} from '@vue/composition-api';

import { onClickOutside } from './utils/onClickOutside';

import { BreadcrumbItem } from './types';
import SdsBreadcrumbItem from './SdsBreadcrumbItem.vue';

import SdsBreadcrumbsDropdownBoxToggle from './SdsBreadcrumbsDropdownBoxToggle.vue';
import SdsBreadcrumbsDropdownBox from './SdsBreadcrumbsDropdownBox.vue';
import { useAvailableBreadcrumbWidthCalculations } from './useAvailableBreadcrumbWidthCalculations';
import { useKeydownEventListener } from '@shared/composables/useKeydownEventListener';

// @TODO: Find a way to do this dynamically, e.g. substract action items width from available parent width before rendering breadcrumbs
const BREADCRUMBS_MAX_WIDTH_DESKTOP = 700;
const BREADCRUMBS_MAX_WIDTH_MOBILE = 250;

export default defineComponent({
    name: 'SdsBreadcrumbs',
    props: {
        items: {
            type: Array as PropType<BreadcrumbItem[]>,
            required: true,
        },
        isLastItemInteractive: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { slots }) {
        const dropdownBoxRef = ref<ComponentPublicInstance | null>(null);
        const {
            breadcrumbsListRef,
            availableBreadcrumbWidth,
        } = useAvailableBreadcrumbWidthCalculations();

        const hasSlot = (name: string) => !!slots[name];

        const truncatedItems = ref<BreadcrumbItem[]>([]);
        const nonTruncatedItems = ref<BreadcrumbItem[]>([...props.items]);

        const isDropdownBoxVisible = ref(false);
        const isDropdownBoxToggleVisible = ref(false);

        const breadcrumbsMaxWidth = computed(() => {
            if (availableBreadcrumbWidth.value) {
                return availableBreadcrumbWidth.value;
            }

            return isMobileLayout() ? BREADCRUMBS_MAX_WIDTH_MOBILE : BREADCRUMBS_MAX_WIDTH_DESKTOP;
        });

        const hasLastIcon = computed(() => hasSlot('last-icon'));

        const hasDropdownBoxToggle = computed(() => truncatedItems.value.length > 0);

        const handleDropdownBoxToggleClick = () => {
            isDropdownBoxVisible.value = !isDropdownBoxVisible.value;
        };

        onClickOutside(dropdownBoxRef, () => {
            isDropdownBoxVisible.value = false;
        });

        const handleEscKeydown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                isDropdownBoxVisible.value = false;
            }
        };

        useKeydownEventListener(handleEscKeydown);

        const handleResize = async () => {
            await nextTick();
            const breadcrumbsElementWidths = Array.from(
                breadcrumbsListRef.value?.children ?? [],
            ).map((node) => node.clientWidth);

            if (breadcrumbsElementWidths.length === 0) {
                return;
            }

            // We need to work right to left, since we hide left first and
            // show as much as possible on the right
            breadcrumbsElementWidths.reverse();

            let maxAvailableWidth = breadcrumbsMaxWidth.value;

            // If the toggle is present, we need to discount that element, as it's also a sibling but does not count
            // It would normally be the first one, but we just reversed the array, so going from the end.
            if (hasDropdownBoxToggle.value) {
                maxAvailableWidth -= breadcrumbsElementWidths[breadcrumbsElementWidths.length - 1]!;
                breadcrumbsElementWidths.pop();
            }

            const availableWidthData = {
                currentWidth: 0,
                elementCount: 0,
            };

            for (const width of breadcrumbsElementWidths) {
                if (availableWidthData.currentWidth + width > maxAvailableWidth) {
                    break;
                }

                availableWidthData.currentWidth += width;
                availableWidthData.elementCount += 1;
            }

            // Always show at least one element, and no need to complicate if we can show all
            if (availableWidthData.elementCount <= 0 || nonTruncatedItems.value.length <= availableWidthData.elementCount) {
                return;
            }

            const removedCount = nonTruncatedItems.value.length - availableWidthData.elementCount;
            truncatedItems.value = [
                ...truncatedItems.value,
                ...nonTruncatedItems.value.slice(0, removedCount),
            ];
            nonTruncatedItems.value = nonTruncatedItems.value.slice(removedCount);
        };

        watch(() => props.items, (newItems, oldItems) => {
            const itemLabels = newItems.map((item) => item.label).join('|');
            const oldItemLabels = (oldItems ?? []).map((item) => item.label).join('|');
            if (itemLabels !== oldItemLabels) {
                nonTruncatedItems.value = [...props.items];
                truncatedItems.value = [];
                handleResize();
            }
        }, { immediate: true, flush: 'post' });

        watch(breadcrumbsMaxWidth, (currentMaxWidth, previousMaxWidth) => {
            const hasTruncated = truncatedItems.value.length > 0;

            if (previousMaxWidth == null) {
                handleResize();
                return;
            }

            if (currentMaxWidth === previousMaxWidth) {
                return;
            }

            if (currentMaxWidth < previousMaxWidth && nonTruncatedItems.value.length === 1) {
                // We can't truncate further
                return;
            }

            // If we have more breathing room, let's try again
            if (currentMaxWidth > previousMaxWidth && hasTruncated) {
                nonTruncatedItems.value = [...props.items];
                truncatedItems.value = [];
            }

            handleResize();
        }, { immediate: true, flush: 'post' });

        return {
            breadcrumbsListRef,
            dropdownBoxRef,
            truncatedItems,
            nonTruncatedItems,
            isDropdownBoxVisible,
            isDropdownBoxToggleVisible,
            handleDropdownBoxToggleClick,
            hasLastIcon,
            hasDropdownBoxToggle,
        };
    },
    components: {
        SdsBreadcrumbItem,
        SdsBreadcrumbsDropdownBoxToggle,
        SdsBreadcrumbsDropdownBox,
        SdsFlag,
    },
});
